/**
 * Token validation and management helpers
 */

/**
 * Check if a token is still valid based on expiration time
 * @param {number} tokenExpires - Token expiration timestamp
 * @param {number} bufferMs - Buffer time in milliseconds (default: 30 seconds)
 * @returns {boolean} - True if token is still valid
 */
export const isTokenValid = (tokenExpires, bufferMs = 30000) => {
  if (!tokenExpires) return false;
  const timeLeft = tokenExpires - Date.now();
  return timeLeft > -bufferMs; // Allow small buffer for network delays
};

/**
 * Check if a token is expired
 * @param {number} tokenExpires - Token expiration timestamp
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = (tokenExpires) => {
  if (!tokenExpires) return true;
  return tokenExpires < Date.now();
};

/**
 * Get time left on token in milliseconds
 * @param {number} tokenExpires - Token expiration timestamp
 * @returns {number} - Time left in milliseconds (negative if expired)
 */
export const getTimeLeftOnToken = (tokenExpires) => {
  if (!tokenExpires) return -1;
  return tokenExpires - Date.now();
};

/**
 * Check if token should be refreshed
 * @param {number} tokenExpires - Token expiration timestamp
 * @param {number} refreshBuffer - Time before expiration to refresh (default: 5 minutes)
 * @returns {boolean} - True if token should be refreshed
 */
export const shouldRefreshToken = (tokenExpires, refreshBuffer = 300000) => {
  if (!tokenExpires) return false;
  const timeLeft = getTimeLeftOnToken(tokenExpires);
  return timeLeft > 0 && timeLeft < refreshBuffer;
};

/**
 * Safe logout that only calls API if token might still be valid
 * @param {Function} logoutApiCall - Function that calls the logout API
 * @param {Function} localLogout - Function that clears local auth state
 * @param {number} tokenExpires - Token expiration timestamp
 * @returns {Promise} - Promise that resolves when logout is complete
 */
export const safeLogout = async (logoutApiCall, localLogout, tokenExpires) => {
  try {
    // Only call API if token might still be valid
    if (isTokenValid(tokenExpires)) {
      await logoutApiCall();
    }
  } catch (error) {
    // Ignore 401 errors as they're expected for expired tokens
    if (error.response?.status !== 401) {
      console.error('Logout API error:', error);
    }
  } finally {
    // Always clear local state
    localLogout();
  }
};

/**
 * Setup axios interceptor to handle token expiration globally
 * @param {Object} axiosInstance - Axios instance to add interceptor to
 * @param {Function} onTokenExpired - Callback when token is expired
 */
export const setupTokenInterceptor = (axiosInstance, onTokenExpired) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token is expired or invalid
        onTokenExpired();
      }
      return Promise.reject(error);
    }
  );
};
